import { checkForNaN, checkValidObject } from "GenericForms/Helper";
import apiGlobal from "global/api.global";
import { FUELLCV, FuelTypes, simulatorOperationsConstant } from "shared/constants";
import { queryKeyes } from "shared/queryKeys";

/** Type for Simulator Operations  */
interface simulatorOperationsType {
  operation: string;
  field: string;
  consumptionLabel: string | any;
  steaming: boolean;
}

/** Operations to consider for Simulator */
export const simulatorOperations: simulatorOperationsType[] = [
  {
    operation: "Anchorage",
    field: "anchorage_percentage",
    consumptionLabel: simulatorOperationsConstant.anchorage,
    steaming: false,
  },
  {
    operation: "Drifting",
    field: "drifting_percentage",
    consumptionLabel: simulatorOperationsConstant.drifting,
    steaming: false,
  },
  {
    operation: "Manoeuvering",
    field: "manoeuvering_percentage",
    consumptionLabel: simulatorOperationsConstant.manoeuvering,
    steaming: false,
  },
  {
    operation: "Port stay(loading)",
    field: "port_stay_loading_percentage",
    consumptionLabel: simulatorOperationsConstant.ps_loading,
    steaming: false,
  },
  {
    operation: "Port stay(discharging)",
    field: "port_stay_discharging_percentage",
    consumptionLabel: simulatorOperationsConstant.ps_discharging,
    steaming: false,
  },
  {
    operation: "Port stay(idle)",
    field: "port_stay_idle_records_percentage",
    consumptionLabel: simulatorOperationsConstant.ps_idle,
    steaming: false,
  },
  {
    operation: "Ballast",
    field: "ballast_percentage",
    consumptionLabel: simulatorOperationsConstant.ballast,
    steaming: true,
  },
  {
    operation: "Laden",
    field: "laden_percentage",
    consumptionLabel: simulatorOperationsConstant.laden,
    steaming: true,
  },
];

/** values of Reduction factor for the year for Required CII */
export const reductionFactor = [
  { year: 2023, z: 5 },
  { year: 2024, z: 7 },
  { year: 2025, z: 9 },
  { year: 2026, z: 11 },
  { year: 2027, z: 13.625 },
  { year: 2028, z: 16.250 },
  { year: 2029, z: 18.875 },
  { year: 2030, z: 21.5 },
];

/** Calculate operation specific DOeq consumption */
export const calculateOpDoeq = (
  obj: any,
  key: string,
  op: string,
  meConsumption: number
): number => {
  let total = 0;
  if (checkValidObject(obj)) {
    Object?.keys(obj)?.forEach((arrayKey) => {
      const array = obj[arrayKey];
      if (Array.isArray(array)) {
        array.forEach((item) => {
          if (typeof item[key] === "number") {
            total += item[key] as number;
          }
        });
      }
    });
    if (op === "ballast" || op === "laden") {
      return checkForNaN(total + meConsumption);
    } else {
      return checkForNaN(total);
    }
  } else {
    return null;
  }
};

/** Calculate DOeq of steaming & non-steaming duration */
export const calculateSteamingNonSteamingTimeDOeq = (
  values: any,
  simulatorOperations: any[],
  steamingPeriod: boolean | null,
  setFieldValue: any
) => {
  let total = 0;
  let steamingTime = 0;
  let nonSteamingTime = 0;
  if (steamingPeriod === null) {
    simulatorOperations?.filter((item: any) => item.steaming === true)?.forEach((op: any) => {
      steamingTime += (values[`${op?.consumptionLabel}_do_eq`] ?? 0);
    });
    setFieldValue("steaming_time_do_eq", steamingTime);
    simulatorOperations?.filter((item: any) => item.steaming === false)?.forEach((op: any) => {
      nonSteamingTime += (values[`${op?.consumptionLabel}_do_eq`] ?? 0);
    });
    setFieldValue("non_steaming_time_do_eq", nonSteamingTime);
  } else {
    simulatorOperations?.filter((item: any) => item.steaming === steamingPeriod)?.forEach((op: any) => {
      total += (values[`${op?.consumptionLabel}_do_eq`] ?? 0);
    });
    if (steamingPeriod === false) {
      setFieldValue("non_steaming_time_do_eq", checkForNaN(total));
    } else {
      setFieldValue("steaming_time_do_eq", checkForNaN(total));
    }
  }
};

/** Calculate total DOeq consumption */
export const calculateTotalDOeqConsumed = (values: any, setFieldValue: any) => {
  let total = 0;
  simulatorOperations?.forEach((op: any) => {
    total += values[`${op?.consumptionLabel}_do_eq`] * values[`${op?.consumptionLabel}_duration`];
  });
  setFieldValue("calculated_do_eq_consumption", checkForNaN(total));
  setFieldValue("total_do_eq_consumed", checkForNaN(total + values?.actual_do_eq_consumption));
};

/** Function to calculate total fuel consumed's DOeq
 * Every applicable fuel type's DOeq is calculated individually & then their sum is returned as the total DOeq
 */
export const calculateTotalDOeq = async (
  values: any,
  fuelConsumption: any[],
  setFieldValue: any,
  do_equivalent: string | null,
  fieldValue: boolean,
  precedenceField: string,
  consumptionField: string,
  simulator: boolean = true,
) => {
  let HFOConsumption = 0;
  let LFOConsumption = 0;
  let DOeq: number = 0;
  let FuelLCVMaster: any;
  await apiGlobal.get(queryKeyes.masters.FuelMultiplyingFactor.url()).then(res => {
    FuelLCVMaster = res.data;
  }).catch(err => {
    console.error(err);
  })
  fuelConsumption?.filter((_item: any, idx: number) => idx < fuelConsumption?.length - 1)?.forEach((fuel: any) => {
    let fuelTypeLCV = FuelLCVMaster?.filter((item: any) => item.fuel_type_precedence_id === fuel[precedenceField])[0]?.lcv * 1000000;
    if (fuel[precedenceField] === FuelTypes.FUEL_OIL) {
      /** To calulate FO's DOeq, first HFO & LFO's DOeq is calulated individually based on their consumption percentage &
       *  then their sum is returned as the total FO's DOeq */
      if (simulator) {
        HFOConsumption = (checkForNaN(fuel[consumptionField]) * values?.HFOPercentage) / 100;
        LFOConsumption = (checkForNaN(fuel[consumptionField]) * values?.LFOPercentage) / 100;
        DOeq += (((HFOConsumption * FUELLCV.HFO) + (LFOConsumption * FUELLCV.LFO)) / FUELLCV.DO);
        if (fieldValue && simulator) {
          setFieldValue(do_equivalent, (((HFOConsumption * FUELLCV.HFO) + (LFOConsumption * FUELLCV.LFO)) / FUELLCV.DO));
        }
      } else {
        DOeq += (((checkForNaN(fuel[consumptionField]) * FUELLCV.HFO)) / FUELLCV.DO);
      }
    } else if (fuel[precedenceField] === FuelTypes.DIESEL_OIL) {
      DOeq += checkForNaN(fuel[consumptionField]);
      if (fieldValue && simulator) {
        setFieldValue(do_equivalent, checkForNaN(fuel[consumptionField]));
      }
    } else if (fuel[precedenceField] !== undefined || (fuel[precedenceField] !== FuelTypes.DIESEL_OIL
      && fuel[precedenceField] !== FuelTypes.FUEL_OIL)) {
      if (simulator) {
        DOeq += ((checkForNaN(fuel[consumptionField] ?? 0) * fuelTypeLCV) / FUELLCV.DO);
        if (fieldValue && simulator) {
          setFieldValue(do_equivalent, ((checkForNaN(fuel[consumptionField]) * fuelTypeLCV) / FUELLCV.DO));
        }
      } else {
        DOeq += ((checkForNaN(fuel[consumptionField]) * fuelTypeLCV) / FUELLCV.DO);
      }
    }
    if (!fieldValue && simulator) {
      setFieldValue('calculated_do_eq_consumption', checkForNaN(DOeq));
      setFieldValue('total_do_eq_consumed', checkForNaN(DOeq));
    }
  })
  if (simulator) {
    return checkForNaN(DOeq);
  }
  return {
    FuelLCVMaster,
    DOeq: checkForNaN(DOeq)
  };
}

export const calculateAttainedCII = (
  values: any,
  fuelDistribution: boolean,
  fuelTypes: any[],
  setFieldValue?: any
) => {
  let total: any = 0;
  if (fuelDistribution) {
    total = calculateTotalDOeq(
      values,
      values?.fuel_distribution,
      setFieldValue,
      null,
      false,
      'fuel_type_precedence_id',
      'fuel_consumption'
    )
  } else {
    simulatorOperations?.forEach((op: any) => {
      total +=
        values[`${op?.consumptionLabel}_do_eq`] *
        values[`${op?.consumptionLabel}_duration`];
    });
  }
  const totalConsumption = total + values?.actual_do_eq_consumption;
  let totalEmission: number = 0;
  fuelTypes?.forEach((fuelType: any) => {
    totalEmission += (
      checkForNaN((checkForNaN(totalConsumption) *
        checkForNaN(values?.consumption_percentage?.[`${fuelType?.fuel_type_short_name}_consumption_percentage`] / 100)) *
        checkForNaN(fuelType.fuel_type_value?.cfco2, 3))
    )
  })
  return (
    totalEmission /
    (values?.fi *
      values?.fm *
      values?.fivse *
      values?.capacity *
      (values?.distance_travelled + values?.vessel_stay?.distance_to_travel))
  );
};

export const calculateReferenceCII = (values: any) => {
  let a: number = 0;
  let c: number = 0;
  let capacity: number = 0;
  if (values?.type === "Bulk Carrier" && values?.dwt >= 279000) {
    a = 4745;
    c = 0.622;
    capacity = 279000;
  } else if (values?.type === "Bulk Carrier" && values?.dwt < 279000) {
    a = 4745;
    c = 0.622;
    capacity = values?.dwt;
  } else if (values?.type === "Gas Carrier" && values?.dwt >= 65000) {
    a = 144050000000;
    c = 2.071;
    capacity = values?.dwt;
  } else if (values?.type === "Gas Carrier" && values?.dwt < 65000) {
    a = 8104;
    c = 0.639;
    capacity = values?.dwt;
  } else if (values?.type === "Tanker") {
    a = 5247;
    c = 0.61;
    capacity = values?.dwt;
  } else if (values?.type === "Container Ship") {
    a = 1984;
    c = 0.489;
    capacity = values?.dwt;
  } else if (values?.type === "General Cargo Ship" && values?.dwt >= 20000) {
    a = 31948;
    c = 0.792;
    capacity = values?.dwt;
  } else if (values?.type === "General Cargo Ship" && values?.dwt < 20000) {
    a = 588;
    c = 0.3885;
    capacity = values?.dwt;
  } else if (values?.type === "Refrigerated Cargo Carrier") {
    a = 4600;
    c = 0.557;
    capacity = values?.dwt;
  } else if (values?.type === "Combination Carrier" && values?.dwt >= 40853) {
    a = 40853;
    c = 0.812;
    capacity = values?.dwt;
  } else if (values?.type === "LNG Carrier" && values?.dwt >= 100000) {
    a = 9.827;
    c = 0;
    capacity = values?.dwt;
  } else if (
    values?.type === "LNG Carrier" &&
    100000 > values?.dwt &&
    values?.dwt >= 65000
  ) {
    a = 144790000000000;
    c = 2.673;
    capacity = values?.dwt;
  } else if (values?.type === "LNG Carrier" && values?.dwt < 65000) {
    a = 144790000000000;
    c = 2.673;
    capacity = 65000;
  } else if (values?.type === "Ro-Ro Cargo Ship(VC)") {
    a = 5739;
    c = 0.631;
    capacity = values?.gt;
  } else if (values?.type === "Ro-Ro Cargo Ship") {
    a = 10952;
    c = 0.637;
    capacity = values?.dwt;
  } else if (values?.type === "Ro-Ro Passenger Ship") {
    a = 7540;
    c = 0.587;
    capacity = values?.gt;
  } else if (values?.type === "Cruise Passenger Ship") {
    a = 930;
    c = 0.383;
    capacity = values?.gt;
  }
  return a * Math.pow(capacity, -c);
};

/** get current year required for Required CII */
export const currentYear = new Date().getFullYear();

export const calculateRequiredCII = (values: any, year: number) => {
  const refCII = calculateReferenceCII(values);
  return refCII * ((100 - checkForNaN(reductionFactor?.find((item: any) => item.year === year)?.z)) / 100);
};

/**
 * function to call consumption from total DOeq
 * @param number
 * @param number
 * @param number
 * @returns number
 */
export const calculateConsumptionFromDOeq = (
  DOeq: number,
  type_precedence_id: number,
  lcv: number,
  values: any
) => {
  let totalConsumption: number;
  if (type_precedence_id === FuelTypes.FUEL_OIL) {
    const HFOConsumption = (((DOeq * values?.HFOPercentage / 100) * FUELLCV.DO) / FUELLCV.HFO);
    const LFOConsumption = (((DOeq * values?.LFOPercentage / 100) * FUELLCV.DO) / FUELLCV.LFO);
    totalConsumption = HFOConsumption + LFOConsumption;
  } else {
    totalConsumption = DOeq * FUELLCV.DO / lcv;
  }
  return checkForNaN(totalConsumption);
}

/**
 * function to calculate graph's Y axis inteval based on max CII ratio
 * @param number
 * @param number
 * @param number
 * @returns number
 */
export const calculateCIIGraphInterval = (maxCIIRatio: number) => {
  if (maxCIIRatio <= 5) {
    return 0.5;
  } else if (maxCIIRatio > 5 && maxCIIRatio <= 10) {
    return 1;
  } else if (maxCIIRatio > 10 && maxCIIRatio <= 20) {
    return 2;
  } else if (maxCIIRatio > 20 && maxCIIRatio <= 30) {
    return 5;
  } else if (maxCIIRatio > 30 && maxCIIRatio <= 50) {
    return 10;
  } else if (maxCIIRatio > 50 && maxCIIRatio <= 100) {
    return 20;
  } else {
    return 50;
  }
}